import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type WarehouseZoneFormSchema, warehouseZoneFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { WarehouseZoneForm } from "@/forms/warehouse-zone-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditWarehouseZoneModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
	warehouseZoneId: string;
}

export function EditWarehouseZoneModal({
	onClose,
	isOpen,
	onComplete,
	warehouseZoneId,
}: EditWarehouseZoneModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.stock["warehouse-zones"][":warehouseZoneId"].$get({
			param: { warehouseZoneId },
		});
		const data = await responseHandler(response);
		return warehouseZoneFormSchema.parse(data.warehouseZone);
	});
	const methods = useForm({
		resolver: resolver(warehouseZoneFormSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: WarehouseZoneFormSchema) {
		const response = await client.stock["warehouse-zones"][":warehouseZoneId"].$patch({
			param: { warehouseZoneId },
			json,
		});
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.edit_warehouse_zone")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<WarehouseZoneForm warehouseZoneId={warehouseZoneId} />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
